import moment from 'moment';
import { useCallback } from 'react';
import { useEntitySelection } from '@shared/api/providers/entitySelection';
import { useToast } from '@shared/components/toast';
import { Entity, toCreateWorkflowEntity } from '@shared/data/entity/Entity';
import { DateField } from '@shared/data/entity/fields/DateField';
import { useChannelEventHandlers, useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { MBAlert } from '@shared/util/alert';
import { i18n } from '@shared/util/i18n';
import { useCreateEntitiesMutation } from './useCreateWorkflowEntities';
import { useDeleteEntitiesMutation } from './useDeleteWorkflowEntities';

export function useOnPressDuplicate({ workflowTemplateId }: { workflowTemplateId: string }) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const { selectedEntities } = useEntitySelection();
  const duplicateEntities = useDuplicateEntities({ workflowTemplateId });
  const duplicates = selectedEntities.map(entity =>
    toCreateWorkflowEntity(entity, workflowTemplate),
  );

  return useCallback(async () => {
    await duplicateEntities(duplicates);
  }, [duplicates, duplicateEntities]);
}

export function useOnPressDuplicateToDate({
  workflowTemplateId,
  calendarDateComponentId,
  selectedDate,
}: {
  workflowTemplateId: string;
  calendarDateComponentId: string;
  selectedDate: string;
}) {
  const { selectedEntities } = useEntitySelection();
  const duplicateEntities = useDuplicateEntities({ workflowTemplateId });
  const duplicates = useGetDuplicatesWithDateOffset(
    selectedEntities,
    calendarDateComponentId,
    selectedDate,
    workflowTemplateId,
  );

  return useCallback(async () => {
    await duplicateEntities(duplicates);
  }, [duplicates, duplicateEntities]);
}

function useGetDuplicatesWithDateOffset(
  entities: Entity[],
  calendarDateComponentId: string,
  selectedDate: string,
  workflowTemplateId: string,
): MBAPI.CreateWorkflowEntity[] {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const dates = entities.map(entity => getEntityDate(entity, calendarDateComponentId));
  const sortedDates = dates.sort();
  const firstDateString = sortedDates[0];

  if (!firstDateString) {
    return [];
  }

  const firstDate = moment(firstDateString).startOf('day');
  const daysDiff = moment(selectedDate).startOf('day').diff(firstDate, 'days');

  return entities.map(entity => {
    const entiyDate = getEntityDate(entity, calendarDateComponentId);
    const shiftedDate = moment(entiyDate).add(daysDiff, 'days').toISOString();
    const updatedEntity = toCreateWorkflowEntity(entity, workflowTemplate);

    return {
      ...updatedEntity,
      fields: {
        ...updatedEntity.fields,
        [calendarDateComponentId]: shiftedDate,
      },
    };
  });
}

function getEntityDate(entity: Entity, calendarDateComponentId: string): string {
  const dateField = entity.allFields.get(calendarDateComponentId) as DateField;
  return dateField.date;
}

export function useDuplicateEntities({ workflowTemplateId }: { workflowTemplateId: string }) {
  const toast = useToast();
  const createEntities = useCreateEntitiesMutation(workflowTemplateId);
  const { onOpenCard } = useChannelEventHandlers();
  const { reset, startLoading, stopLoading } = useEntitySelection();

  return useCallback(
    async (entities: MBAPI.CreateWorkflowEntity[]) => {
      startLoading();
      try {
        const createdEntities = await createEntities(entities);
        const firstEntity = createdEntities[0];

        toast.show({
          icon: 'check-filled',
          message: i18n('card.duplicated', { count: entities.length }),
          actions:
            createdEntities.length === 1
              ? [
                  {
                    label: 'view',
                    onPress: () => onOpenCard(firstEntity),
                  },
                ]
              : undefined,
        });
        reset();
      } catch (e) {
        console.error(e);
      } finally {
        stopLoading();
      }
    },
    [createEntities, reset, toast, startLoading, stopLoading, onOpenCard],
  );
}

export function useOnPressDelete({ workflowTemplateId }: { workflowTemplateId: string }) {
  const toast = useToast();
  const deleteEntities = useDeleteEntitiesMutation(workflowTemplateId);
  const { selectedEntities, selectedCount, reset, startLoading, stopLoading } =
    useEntitySelection();

  return useCallback(async () => {
    const confirmed = await MBAlert.confirm({
      message: i18n('card.actions.deleteConfirmationCount', { count: selectedCount }),
      confirmText: i18n('common.delete'),
    });
    if (confirmed) {
      startLoading();
      const ids = selectedEntities.map(item => item.id);
      try {
        await deleteEntities(ids);
        toast.show({
          icon: 'check-filled',
          message: i18n('card.deleted', { count: selectedCount }),
        });
        reset();
      } catch (e) {
        console.error(e);
      } finally {
        stopLoading();
      }
    }
  }, [deleteEntities, selectedEntities, selectedCount, reset, toast, startLoading, stopLoading]);
}
